/**
 * How tall the map drawer settles at, in one place, because two of them need it: the drawer to lay
 * itself out, and the camera to know how much of the map it is about to cover.
 *
 * Pure numbers on purpose — the state slice reaches for this, so it must not pull the drawer, the
 * tab bar or maplibre into its module graph.
 */

/** What the drawer keeps around its header, and so the gap above the search field as well. */
export const DRAWER_PADDING = 16;

/** The themed height of the search field's visible box (`MuiOutlinedInput`). */
export const SEARCH_FIELD_HEIGHT = 44;

/** The margin that box carries above itself, which the wrapper has to be lifted by. */
export const SEARCH_FIELD_MARGIN_TOP = 12;

/** The rate the search chrome, and the drawer's height while searching, animate at. */
export const SEARCH_TRANSITION = "0.25s ease";

/** The field padded evenly on all four sides — the whole drawer with the results window shut. */
export const SEARCH_HEADER_HEIGHT = SEARCH_FIELD_HEIGHT + DRAWER_PADDING * 2;

/** One `MapSearchResultRow`: 12px of padding around a 24px + 16px two-line label, plus its divider. */
export const MAP_SEARCH_ROW_HEIGHT = 12 * 2 + 24 + 16 + 1;

/** How far the results window opens. Enough to show there is a list; the rest is a scroll away. */
export const MAP_SEARCH_VISIBLE_ROWS = 2;

export interface MapDrawerHeightInput {
  searchIsOpen: boolean;
  searchQuery: string;
  /** Hits for the current query. The window opens to at most `MAP_SEARCH_VISIBLE_ROWS` of them. */
  searchResultCount: number;
  /** An itinerary marker or a stand. A team member gets the short drawer, as it always has. */
  hasSelection: boolean;
  /** The map's own height in px, from the top of its canvas down to the tab bar the drawer sits on. */
  mapHeight: number;
  /** That tab bar, which the drawer's CSS has to take off its container's height to match. */
  tabBarHeight: number;
}

/** How much of the map a selection's drawer takes, and how much an empty one takes. */
const SELECTION_FRACTION = 0.5;
const EMPTY_FRACTION = 0.25;

/**
 * The height the drawer ends its transition at, as the CSS the drawer writes and the pixels the
 * camera reasons in. Both come off the same branch so they cannot drift apart.
 */
export function mapDrawerHeight(input: MapDrawerHeightInput): { css: string; px: number } {
  const { searchIsOpen, searchQuery, searchResultCount, hasSelection, mapHeight, tabBarHeight } = input;

  // The keyboard comes up under the search field, so everything below it is covered: the drawer
  // stays field-height until there is a query to answer, then opens a window onto the hits. It takes
  // no more of the map than it has rows to put there — a lone hit gets one row, not two with a gap
  // under it — and anything past the second scrolls. A query with no hits keeps a row for the line
  // that says so.
  if (searchIsOpen) {
    const rows = searchQuery.trim() ? Math.min(Math.max(searchResultCount, 1), MAP_SEARCH_VISIBLE_ROWS) : 0;
    const px = SEARCH_HEADER_HEIGHT + rows * MAP_SEARCH_ROW_HEIGHT;

    return { css: `${px}px`, px };
  }

  // A share of the map rather than of the window: the map starts below the header, so half the
  // window left a phone a strip of map too thin to show what had just been picked.
  const fraction = hasSelection ? SELECTION_FRACTION : EMPTY_FRACTION;

  return { css: `calc((100% - ${tabBarHeight}px) * ${fraction})`, px: mapHeight * fraction };
}
